import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  scanReceiptStorage,
  scanItemStorage,
  scanTagStorage,
  getItemImageUrls,
  getStorageProvider,
  isCloudinaryUrl,
  isR2Url,
  migrateSingleReceipt,
  batchMigrateReceipts,
  migrateSingleItem,
  batchMigrateItems,
  migrateSingleTag,
  batchMigrateTags,
  replaceReceiptImage,
  checkR2WorkerHealth,
  getR2WorkerUrl,
  getR2AuthKey,
  saveR2WorkerConfig,
  clearR2WorkerConfig,
  fetchCloudInfrastructureStats,
} from '../lib/migrationHelper';
import {
  BACKUP_TABLES,
  createDatabaseBackup,
  uploadBackupToR2,
  downloadBackupLocally,
  getLastBackupInfo,
  saveLastBackupInfo,
} from '../lib/backupHelper';

const StorageAdminPage = ({
  supabase,
  setSuccessModal,
  user,
  isMobile,
  fetchData,
}) => {
  // 1. Data States
  const [assetCategory, setAssetCategory] = useState('receipts'); // 'receipts' | 'items' | 'tags'
  const [receipts, setReceipts] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const receiptsRef = useRef(receipts);
  receiptsRef.current = receipts;

  // 2. Health Check State
  const [workerHealth, setWorkerHealth] = useState({ online: false, message: 'รอตรวจสอบ' });
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [infraStats, setInfraStats] = useState(null);
  const [isCheckingInfra, setIsCheckingInfra] = useState(false);
  const [showSecretModal, setShowSecretModal] = useState(false);

  // 3. Migration States
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState({
    current: 0,
    total: 0,
    item: null,
    status: '',
  });
  const [singleMigratingId, setSingleMigratingId] = useState(null);

  // 4. Filter & Search States
  const [filterTab, setFilterTab] = useState('all'); // 'all' | 'cloudinary' | 'r2'
  const [searchQuery, setSearchQuery] = useState('');

  // 5. Modal States
  const [zoomedImage, setZoomedImage] = useState(null); // { url, receiptNumber }
  const [replaceTarget, setReplaceTarget] = useState(null); // receipt object
  const [replacementFile, setReplacementFile] = useState(null);
  const [replacementPreview, setReplacementPreview] = useState('');
  const [isReplacing, setIsReplacing] = useState(false);
  const [replaceError, setReplaceError] = useState('');

  // 6. Worker URL Configuration Modal States
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [workerUrlInput, setWorkerUrlInput] = useState('');
  const [workerAuthKeyInput, setWorkerAuthKeyInput] = useState('');
  const [configSuccessMsg, setConfigSuccessMsg] = useState('');
  const [configErrorMsg, setConfigErrorMsg] = useState('');
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  // 7. Database Backup & Disaster Recovery States
  const [isBackingUpR2, setIsBackingUpR2] = useState(false);
  const [isDownloadingBackup, setIsDownloadingBackup] = useState(false);
  const [backupProgressMsg, setBackupProgressMsg] = useState('');
  const [lastBackup, setLastBackup] = useState(() => getLastBackupInfo());
  const [backupModalData, setBackupModalData] = useState(null);
  const [showDrModal, setShowDrModal] = useState(false);

  // ==========================================
  // Check R2 Worker Health & Cloud Infra Quota
  // ==========================================
  const handleCheckHealth = useCallback(async (currentReceipts, targetWorkerUrl) => {
    setIsCheckingHealth(true);
    setIsCheckingInfra(true);
    const resolvedUrl = targetWorkerUrl !== undefined ? targetWorkerUrl : getR2WorkerUrl();
    try {
      const targetReceipts = Array.isArray(currentReceipts) ? currentReceipts : (receiptsRef.current || []);
      const [r2Res, infraRes] = await Promise.allSettled([
        checkR2WorkerHealth(resolvedUrl),
        fetchCloudInfrastructureStats(resolvedUrl, supabase, targetReceipts),
      ]);

      if (r2Res.status === 'fulfilled') {
        setWorkerHealth(r2Res.value);
      }
      if (infraRes.status === 'fulfilled') {
        setInfraStats(infraRes.value);
        if (infraRes.value?.r2) {
          setWorkerHealth({
            online: infraRes.value.r2.online,
            message: infraRes.value.r2.message || (infraRes.value.r2.online ? 'Online' : 'Offline'),
          });
        }
      }
    } catch (err) {
      setWorkerHealth({ online: false, message: err.message || 'Worker Unreachable' });
    } finally {
      setIsCheckingHealth(false);
      setIsCheckingInfra(false);
    }
  }, [supabase]);

  // ==========================================
  // Worker URL Config Handlers
  // ==========================================
  const handleOpenConfigModal = useCallback(() => {
    const curUrl = getR2WorkerUrl();
    const curKey = getR2AuthKey();
    setWorkerUrlInput(curUrl || '');
    setWorkerAuthKeyInput(curKey || '');
    setConfigSuccessMsg('');
    setConfigErrorMsg('');
    setShowConfigModal(true);
  }, []);

  const handleSaveConfig = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setConfigErrorMsg('');
    setConfigSuccessMsg('');

    const trimmedUrl = (workerUrlInput || '').trim();
    const trimmedKey = (workerAuthKeyInput || '').trim();

    if (trimmedUrl && !trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
      setConfigErrorMsg('กรุณาระบุ URL ที่ถูกต้อง ขึ้นต้นด้วย https:// หรือ http://');
      return;
    }

    setIsSavingConfig(true);

    try {
      saveR2WorkerConfig(trimmedUrl, trimmedKey);

      // Re-check health immediately with the new URL
      const healthResult = await checkR2WorkerHealth(trimmedUrl);
      setWorkerHealth(healthResult);

      await handleCheckHealth(receiptsRef.current, trimmedUrl);

      setConfigSuccessMsg('บันทึกและเชื่อมต่อเรียบร้อยแล้ว!');

      if (typeof setSuccessModal === 'function') {
        setSuccessModal({
          title: 'บันทึกการตั้งค่าสำเร็จ! ⚙️',
          message: trimmedUrl
            ? `กำหนด URL ของ Worker (${trimmedUrl}) เรียบร้อยแล้ว (สถานะ: ${healthResult.online ? '🟢 Online' : '🔴 Offline'})`
            : 'ล้าง URL ของ Worker ใน LocalStorage เรียบร้อยแล้ว',
        });
      }

      setTimeout(() => {
        setShowConfigModal(false);
      }, 700);
    } catch (err) {
      setConfigErrorMsg(`เกิดข้อผิดพลาดในการบันทึก: ${err.message || err}`);
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleClearConfig = async () => {
    clearR2WorkerConfig();
    const defaultUrl = getR2WorkerUrl();
    const defaultKey = getR2AuthKey();
    setWorkerUrlInput(defaultUrl);
    setWorkerAuthKeyInput(defaultKey);
    setConfigSuccessMsg('ล้างค่าใน LocalStorage เรียบร้อย (คืนค่าจาก .env หรือค่าว่าง)');
    await handleCheckHealth(receiptsRef.current, defaultUrl);
  };

  // Manual refresh handler for health and infra stats
  const handleManualRefresh = useCallback(() => {
    handleCheckHealth();
  }, [handleCheckHealth]);

  // ==========================================
  // Fetch Receipts from Supabase (strictly once on mount)
  // ==========================================
  const loadReceipts = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');
    let list = [];
    try {
      if (!supabase) {
        throw new Error('Supabase client is not available');
      }

      const { data, error } = await supabase
        .from('receipts')
        .select('*')
        .order('received_date', { ascending: false });

      if (error) throw error;
      list = data || [];
      setReceipts(list);
    } catch (err) {
      console.error('[StorageAdminPage] Failed to load receipts:', err);
      setErrorMessage(err.message || 'ไม่สามารถโหลดข้อมูลใบเสร็จได้');
    } finally {
      setIsLoading(false);
      handleCheckHealth(list);
    }
  }, [supabase, handleCheckHealth]);

  const loadItems = useCallback(async () => {
    try {
      if (!supabase) return;
      const { data, error } = await supabase
        .from('item_master')
        .select('*')
        .order('item_name', { ascending: true });
      if (error) throw error;
      setItemsList(data || []);
    } catch (err) {
      console.error('[StorageAdminPage] Failed to load items:', err);
    }
  }, [supabase]);

  const loadTags = useCallback(async () => {
    try {
      if (!supabase) return;
      const { data, error } = await supabase
        .from('instrument_tags')
        .select('*')
        .order('id', { ascending: true });
      if (error) throw error;
      setTagsList(data || []);
    } catch (err) {
      console.error('[StorageAdminPage] Failed to load tags:', err);
    }
  }, [supabase]);

  // Load receipts strictly once when page mounts
  useEffect(() => {
    loadReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch items or tags on demand when switching category
  useEffect(() => {
    if (assetCategory === 'items') {
      loadItems();
    } else if (assetCategory === 'tags') {
      loadTags();
    }
  }, [assetCategory, loadItems, loadTags]);

  // ==========================================
  // Storage Stats Calculation
  // ==========================================
  const receiptStats = useMemo(() => scanReceiptStorage(receipts), [receipts]);
  const itemStats = useMemo(() => scanItemStorage(itemsList), [itemsList]);
  const tagStats = useMemo(() => scanTagStorage(tagsList), [tagsList]);

  const stats = useMemo(() => {
    if (assetCategory === 'items') return itemStats;
    if (assetCategory === 'tags') return tagStats;
    return receiptStats;
  }, [assetCategory, receiptStats, itemStats, tagStats]);

  const r2Percent = stats.total > 0 ? Math.round((stats.r2Count / stats.total) * 100) : 0;
  const cloudinaryPercent = stats.total > 0 ? Math.round((stats.cloudinaryCount / stats.total) * 100) : 0;

  // ==========================================
  // Filtered List
  // ==========================================
  const filteredReceipts = useMemo(() => {
    return receipts.filter((rc) => {
      // 1. Filter Tab
      if (filterTab === 'cloudinary' && !isCloudinaryUrl(rc.receipt_image_url)) {
        return false;
      }
      if (filterTab === 'r2' && !isR2Url(rc.receipt_image_url)) {
        return false;
      }

      // 2. Search Query
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      const numMatch = rc.receipt_number?.toLowerCase().includes(q);
      const poMatch = rc.po_number?.toLowerCase().includes(q);
      const supplierMatch = rc.supplier_name?.toLowerCase().includes(q);
      const dateMatch = rc.received_date?.toLowerCase().includes(q);

      return numMatch || poMatch || supplierMatch || dateMatch;
    });
  }, [receipts, filterTab, searchQuery]);

  const filteredItems = useMemo(() => {
    return itemsList.filter((it) => {
      const rawUrls = getItemImageUrls(it?.image_url);
      const urls = Array.isArray(rawUrls) ? rawUrls : [];
      const hasCloudinary = urls.some((u) => isCloudinaryUrl(u));
      const allR2 = urls.length > 0 && urls.every((u) => isR2Url(u));

      if (filterTab === 'cloudinary' && !hasCloudinary) return false;
      if (filterTab === 'r2' && !allR2) return false;

      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      const nameMatch = it.item_name?.toLowerCase().includes(q);
      const catMatch = it.category?.toLowerCase().includes(q);
      const barcodeMatch = it.barcode?.toLowerCase().includes(q);
      const aliasMatch = Array.isArray(it.aliases)
        ? it.aliases.some((a) => a?.toLowerCase().includes(q))
        : typeof it.aliases === 'string' && it.aliases.toLowerCase().includes(q);
      return nameMatch || catMatch || barcodeMatch || aliasMatch;
    });
  }, [itemsList, filterTab, searchQuery]);

  const filteredTags = useMemo(() => {
    return tagsList.filter((tg) => {
      if (filterTab === 'cloudinary' && !isCloudinaryUrl(tg.image_url)) return false;
      if (filterTab === 'r2' && !isR2Url(tg.image_url)) return false;

      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase().trim();
      const nameMatch = tg.name?.toLowerCase().includes(q);
      const descMatch = tg.description?.toLowerCase().includes(q);
      return nameMatch || descMatch;
    });
  }, [tagsList, filterTab, searchQuery]);

  // ==========================================
  // Batch Migration Handler
  // ==========================================
  const handleStartBatchMigration = async () => {
    const unmigratedItems = stats.unmigrated;
    if (!unmigratedItems.length) {
      alert('ไม่มีรูปภาพที่ต้องโอนย้าย (ทุกรูปอยู่บน Cloudflare R2 แล้ว)');
      return;
    }

    const categoryNames = {
      receipts: 'ใบเสร็จ',
      items: 'รายการวัสดุ (Item Master)',
      tags: 'ป้ายเครื่องมือ (Instrument Tags)',
    };
    const catLabel = categoryNames[assetCategory] || 'รายการ';

    const confirmMsg = `ยืนยันการโอนย้ายรูปภาพ${catLabel} จำนวน ${unmigratedItems.length} รายการ จาก Cloudinary ไปยัง Cloudflare R2 ทันทีหรือไม่?`;
    if (!window.confirm(confirmMsg)) return;

    setIsMigrating(true);
    setMigrationProgress({
      current: 0,
      total: unmigratedItems.length,
      item: null,
      status: 'starting',
    });

    try {
      let summary;
      if (assetCategory === 'items') {
        summary = await batchMigrateItems(
          unmigratedItems,
          supabase,
          (progressInfo) => {
            setMigrationProgress(progressInfo);
          }
        );
      } else if (assetCategory === 'tags') {
        summary = await batchMigrateTags(
          unmigratedItems,
          supabase,
          (progressInfo) => {
            setMigrationProgress(progressInfo);
          }
        );
      } else {
        summary = await batchMigrateReceipts(
          unmigratedItems,
          supabase,
          (progressInfo) => {
            setMigrationProgress(progressInfo);
          }
        );
      }

      // Refresh data
      if (assetCategory === 'items') {
        await loadItems();
      } else if (assetCategory === 'tags') {
        await loadTags();
      } else {
        await loadReceipts();
      }
      if (typeof fetchData === 'function') {
        fetchData();
      }

      if (typeof setSuccessModal === 'function') {
        setSuccessModal({
          title: 'โอนย้ายสำเร็จเรียบร้อย! 🚀',
          message: `ย้ายรูป${catLabel}ไปยัง Cloudflare R2 สำเร็จ ${summary.successCount} รายการ (ล้มเหลว: ${summary.failedCount} รายการ)`,
        });
      } else {
        alert(`โอนย้ายสำเร็จ ${summary.successCount} รายการ (ล้มเหลว: ${summary.failedCount} รายการ)`);
      }
    } catch (err) {
      console.error('[StorageAdminPage] Batch migration error:', err);
      alert(`เกิดข้อผิดพลาดระหว่างโอนย้าย: ${err.message || err}`);
    } finally {
      setIsMigrating(false);
      setMigrationProgress({ current: 0, total: 0, item: null, status: '' });
    }
  };

  // ==========================================
  // Single Asset Migration Handler
  // ==========================================
  const handleMigrateSingle = async (record) => {
    if (!record) return;
    setSingleMigratingId(record.id);

    try {
      if (assetCategory === 'items') {
        await migrateSingleItem(record, supabase);
      } else if (assetCategory === 'tags') {
        await migrateSingleTag(record, supabase);
      } else {
        await migrateSingleReceipt(record, supabase);
      }
      if (assetCategory === 'items') {
        await loadItems();
      } else if (assetCategory === 'tags') {
        await loadTags();
      } else {
        await loadReceipts();
      }
      if (typeof fetchData === 'function') {
        fetchData();
      }

      if (typeof setSuccessModal === 'function') {
        const titleName = record.receipt_number || record.item_name || record.name || record.id;
        setSuccessModal({
          title: 'ย้ายรูปภาพสำเร็จ ⚡',
          message: `รายการ "${titleName}" ย้ายไป Cloudflare R2 เรียบร้อยแล้ว`,
        });
      }
    } catch (err) {
      console.error('[StorageAdminPage] Single migration failed:', err);
      alert(`ไม่สามารถย้ายรูปภาพได้: ${err.message || err}`);
    } finally {
      setSingleMigratingId(null);
    }
  };

  // ==========================================
  // Replace Image Handlers
  // ==========================================
  const openReplaceModal = (receipt) => {
    setReplaceTarget(receipt);
    setReplacementFile(null);
    setReplacementPreview('');
    setReplaceError('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setReplacementFile(file);
    setReplaceError('');

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setReplacementPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setReplacementPreview('');
    }
  };

  const handleConfirmReplace = async () => {
    if (!replaceTarget || !replacementFile) {
      setReplaceError('กรุณาเลือกไฟล์ภาพที่ต้องการแทนที่');
      return;
    }

    setIsReplacing(true);
    setReplaceError('');

    try {
      await replaceReceiptImage(replaceTarget.id, replacementFile, supabase);
      await loadReceipts();
      if (typeof fetchData === 'function') {
        fetchData();
      }

      const receiptNum = replaceTarget.receipt_number || replaceTarget.id;
      setReplaceTarget(null);
      setReplacementFile(null);
      setReplacementPreview('');

      if (typeof setSuccessModal === 'function') {
        setSuccessModal({
          title: 'เปลี่ยนรูปภาพสำเร็จ ✏️',
          message: `อัปโหลดรูปภาพใหม่ของใบเสร็จ #${receiptNum} ไปยัง Cloudflare R2 เรียบร้อยแล้ว`,
        });
      }
    } catch (err) {
      console.error('[StorageAdminPage] Replace image failed:', err);
      setReplaceError(err.message || 'เกิดข้อผิดพลาดในการเปลี่ยนรูปภาพ');
    } finally {
      setIsReplacing(false);
    }
  };

  // ==========================================
  // Image Download Helper
  // ==========================================
  const handleDownloadImage = async (imageUrl, filename = 'receipt-image.jpg') => {
    if (!imageUrl) return;
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Direct download failed, opening in new tab:', err);
      window.open(imageUrl, '_blank');
    }
  };

  // ==========================================
  // Database Backup Handlers (Disaster Recovery)
  // ==========================================
  const handleBackupToR2 = async () => {
    setIsBackingUpR2(true);
    setBackupProgressMsg('กำลังเตรียมสำรองข้อมูล...');
    try {
      if (!supabase) {
        throw new Error('Supabase client is not available');
      }

      const backupResult = await createDatabaseBackup(supabase, (table, current, total) => {
        setBackupProgressMsg(`กำลังดึงข้อมูล [${current}/${total}]: ${table}...`);
      });

      setBackupProgressMsg('กำลังอัปโหลดสแนปช็อตขึ้น Cloudflare R2...');
      const uploadRes = await uploadBackupToR2(backupResult, supabase);

      const backupInfo = {
        timestamp: new Date().toISOString(),
        filename: uploadRes.filename,
        totalRecords: uploadRes.totalRecords,
        tableCounts: backupResult.backupData.tableCounts,
        url: uploadRes.url,
      };

      saveLastBackupInfo(backupInfo);
      setLastBackup(backupInfo);
      setBackupModalData(backupInfo);

      if (typeof setSuccessModal === 'function') {
        setSuccessModal({
          title: 'สำรองฐานข้อมูลสำเร็จ! 💾',
          message: `สำรองข้อมูล 10 ตาราง (${uploadRes.totalRecords.toLocaleString()} รายการ) ขึ้น Cloudflare R2 เรียบร้อยแล้ว`,
        });
      }
    } catch (err) {
      console.error('[StorageAdminPage] Database backup failed:', err);
      alert(`การสำรองข้อมูลล้มเหลว: ${err.message || err}`);
    } finally {
      setIsBackingUpR2(false);
      setBackupProgressMsg('');
    }
  };

  const handleDownloadBackup = async () => {
    setIsDownloadingBackup(true);
    setBackupProgressMsg('กำลังเตรียมไฟล์สำรอง...');
    try {
      if (!supabase) {
        throw new Error('Supabase client is not available');
      }

      const backupResult = await createDatabaseBackup(supabase, (table, current, total) => {
        setBackupProgressMsg(`กำลังดึงข้อมูล [${current}/${total}]: ${table}...`);
      });

      downloadBackupLocally(backupResult);

      const backupInfo = {
        timestamp: new Date().toISOString(),
        filename: backupResult.filename,
        totalRecords: backupResult.totalRecords,
        tableCounts: backupResult.backupData.tableCounts,
        downloadedLocally: true,
      };

      saveLastBackupInfo(backupInfo);
      setLastBackup(backupInfo);

      if (typeof setSuccessModal === 'function') {
        setSuccessModal({
          title: 'ดาวน์โหลดไฟล์สำรองสำเร็จ ⬇️',
          message: `บันทึกไฟล์ ${backupResult.filename} (${backupResult.totalRecords.toLocaleString()} รายการ) ลงในคอมพิวเตอร์ของคุณแล้ว`,
        });
      }
    } catch (err) {
      console.error('[StorageAdminPage] Local backup download failed:', err);
      alert(`การดาวน์โหลดไฟล์สำรองล้มเหลว: ${err.message || err}`);
    } finally {
      setIsDownloadingBackup(false);
      setBackupProgressMsg('');
    }
  };

  return (
    <div style={{ padding: isMobile ? '12px' : '24px 32px', maxWidth: '1300px', margin: '0 auto', fontFamily: 'sans-serif' }}>
      {/* 1. Header Section */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          borderRadius: '16px',
          padding: isMobile ? '20px' : '28px 32px',
          color: '#ffffff',
          marginBottom: '24px',
          boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.3)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '16px' }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px', background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', borderRadius: '20px', fontSize: '0.82em', fontWeight: 'bold', marginBottom: '8px' }}>
              <span>☁️</span> Cloudflare R2 & Cloudinary Management
            </div>
            <h1 style={{ fontSize: isMobile ? '1.4em' : '1.85em', fontWeight: '800', margin: '4px 0 8px 0', letterSpacing: '-0.5px' }}>
              🗄️ ศูนย์จัดการคลังรูปภาพ & ซิงค์ R2
            </h1>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.92em', lineHeight: '1.5' }}>
              ตรวจสอบความจุและความปลอดภัย จัดเก็บไฟล์หลักฐานใบเสร็จบน Cloudflare R2 (Zero Egress Fees) และโอนย้ายข้อมูลจากระบบเดิมอัตโนมัติ
            </p>
          </div>

          <button
            type="button"
            onClick={loadReceipts}
            disabled={isLoading || isMigrating}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '10px',
              fontSize: '0.9em',
              fontWeight: '600',
              cursor: isLoading || isMigrating ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
          >
            <span style={{ display: 'inline-block', transform: isLoading ? 'rotate(180deg)' : 'none', transition: 'transform 0.5s' }}>🔄</span>
            {isLoading ? 'กำลังโหลด...' : 'รีเฟรชข้อมูล'}
          </button>
        </div>
      </div>

      {errorMessage && (
        <div
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#b91c1c',
            padding: '12px 16px',
            borderRadius: '12px',
            marginBottom: '20px',
            fontSize: '0.92em',
          }}
        >
          ⚠️ {errorMessage}
        </div>
      )}

      {/* 1.5. Cloud & Server Health Monitor Section */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: isMobile ? '16px' : '24px',
          border: '1px solid #e2e8f0',
          marginBottom: '24px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* Section Header */}
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: '12px',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.4em' }}>🌐</span>
              <h2 style={{ fontSize: isMobile ? '1.15em' : '1.3em', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                สถานะคลาวด์และโควต้าการใช้งาน (Cloud & Server Health Monitor)
              </h2>
            </div>
            <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.86em' }}>
              ตรวจสอบความหน่วงเครือข่าย (Latency) และปริมาณโควต้าการใช้งานจริงของ Cloudflare R2, Cloudinary และฐานข้อมูล Supabase
            </p>
          </div>

          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={isCheckingHealth || isCheckingInfra}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              backgroundColor: '#f8fafc',
              color: '#334155',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '0.86em',
              fontWeight: '600',
              cursor: isCheckingHealth || isCheckingInfra ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                transform: isCheckingHealth || isCheckingInfra ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.5s',
              }}
            >
              🔄
            </span>
            {isCheckingHealth || isCheckingInfra ? 'กำลังทดสอบสด...' : 'รีเฟรชสถานะสด'}
          </button>
        </div>

        {/* 3 Health Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '16px',
          }}
        >
          {/* Card 1: Cloudflare R2 Storage */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              padding: '18px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '1.2em' }}>☁️</span>
                  <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.95em' }}>
                    Cloudflare R2 Storage
                  </span>
                </div>
                {/* Status Pill */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 10px',
                    borderRadius: '12px',
                    backgroundColor: infraStats?.r2?.online ? '#dcfce7' : (isCheckingHealth || isCheckingInfra ? '#fef3c7' : '#fee2e2'),
                    color: infraStats?.r2?.online ? '#15803d' : (isCheckingHealth || isCheckingInfra ? '#b45309' : '#b91c1c'),
                    fontSize: '0.78em',
                    fontWeight: '700',
                  }}
                >
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: infraStats?.r2?.online ? '#22c55e' : (isCheckingHealth || isCheckingInfra ? '#f59e0b' : '#ef4444'),
                      display: 'inline-block',
                    }}
                  />
                  {infraStats?.r2?.online
                    ? `Online (${infraStats.r2.latencyMs ? `${infraStats.r2.latencyMs} ms` : 'ปกติ'})`
                    : (isCheckingHealth || isCheckingInfra ? 'กำลังตรวจสอบ...' : 'Offline')}
                </div>
              </div>

              {/* Metric */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.8em', color: '#64748b', marginBottom: '4px' }}>
                  พื้นที่จัดเก็บที่ใช้ไป (Storage Used)
                </div>
                <div style={{ fontSize: '1.35em', fontWeight: '800', color: '#0f172a' }}>
                  {infraStats?.r2?.totalFormatted || '0 B'}{' '}
                  <span style={{ fontSize: '0.65em', color: '#64748b', fontWeight: '500' }}>
                    / 10,240 MB (10 GB)
                  </span>
                </div>
              </div>

              {/* Progress Gauge */}
              <div style={{ marginBottom: '14px' }}>
                <div
                  style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, Math.max(0.5, infraStats?.r2?.percentUsed || 0.5))}%`,
                      height: '100%',
                      backgroundColor:
                        (infraStats?.r2?.percentUsed || 0) < 70
                          ? '#10b981'
                          : (infraStats?.r2?.percentUsed || 0) < 90
                          ? '#f59e0b'
                          : '#ef4444',
                      borderRadius: '4px',
                      transition: 'width 0.4s ease',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#64748b', marginTop: '4px' }}>
                  <span>{infraStats?.r2?.percentUsed || 0}% ของโควต้าฟรี</span>
                  <span>ฟรี 10 GB ทุกเดือน</span>
                </div>
              </div>
            </div>

            {/* Badges and Footnote */}
            <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span
                  style={{
                    backgroundColor: '#e0f2fe',
                    color: '#0369a1',
                    fontSize: '0.74em',
                    fontWeight: '700',
                    padding: '2px 8px',
                    borderRadius: '6px',
                  }}
                >
                  ⚡ แบนด์วิธฟรี 100% (Zero Egress)
                </span>
                <span style={{ fontSize: '0.78em', color: '#334155', fontWeight: '600' }}>
                  📁 {infraStats?.r2?.totalObjects || stats.r2Count} ไฟล์ใน Bucket
                </span>
              </div>
              {infraStats?.r2?.folders && (
                <div style={{ fontSize: '0.72em', color: '#64748b' }}>
                  ใบเสร็จ: {infraStats.r2.folders.receipts?.count || 0} ({infraStats.r2.folders.receipts?.formatted || '0 B'})
                  {' • '}
                  ใบสั่งซื้อ: {infraStats.r2.folders.orders?.count || 0}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2px' }}>
                <button
                  type="button"
                  onClick={handleOpenConfigModal}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    padding: '3px 8px',
                    fontSize: '0.74em',
                    fontWeight: '600',
                    color: '#0284c7',
                    cursor: 'pointer',
                  }}
                >
                  ⚙️ ตั้งค่า URL ของ Worker
                </button>
              </div>
            </div>
          </div>

          {/* Card 2: Cloudinary Media CDN */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              padding: '18px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '1.2em' }}>🌤️</span>
                  <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.95em' }}>
                    Cloudinary Media CDN
                  </span>
                </div>
                {/* Status Pill */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 10px',
                    borderRadius: '12px',
                    backgroundColor: infraStats?.cloudinary?.online ? '#dcfce7' : (isCheckingHealth || isCheckingInfra ? '#fef3c7' : '#fee2e2'),
                    color: infraStats?.cloudinary?.online ? '#15803d' : (isCheckingHealth || isCheckingInfra ? '#b45309' : '#b91c1c'),
                    fontSize: '0.78em',
                    fontWeight: '700',
                  }}
                >
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: infraStats?.cloudinary?.online ? '#22c55e' : (isCheckingHealth || isCheckingInfra ? '#f59e0b' : '#ef4444'),
                      display: 'inline-block',
                    }}
                  />
                  {infraStats?.cloudinary?.online
                    ? `Online (${infraStats.cloudinary.latencyMs ? `${infraStats.cloudinary.latencyMs} ms` : 'ปกติ'})`
                    : (isCheckingHealth || isCheckingInfra ? 'กำลังตรวจสอบ...' : 'Offline')}
                </div>
              </div>

              {/* Metric */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.8em', color: '#64748b', marginBottom: '4px' }}>
                  เครดิตที่ใช้ไป (Credits Used)
                </div>
                <div style={{ fontSize: '1.35em', fontWeight: '800', color: '#0f172a' }}>
                  {infraStats?.cloudinary?.credits?.usage || 0}{' '}
                  <span style={{ fontSize: '0.65em', color: '#64748b', fontWeight: '500' }}>
                    / {infraStats?.cloudinary?.credits?.limit || 25} เครดิต
                  </span>
                </div>
              </div>

              {/* Progress Gauge */}
              <div style={{ marginBottom: '14px' }}>
                <div
                  style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, Math.max(0.5, infraStats?.cloudinary?.credits?.percent_usage || 0.5))}%`,
                      height: '100%',
                      backgroundColor:
                        (infraStats?.cloudinary?.credits?.percent_usage || 0) < 70
                          ? '#10b981'
                          : (infraStats?.cloudinary?.credits?.percent_usage || 0) < 90
                          ? '#f59e0b'
                          : '#ef4444',
                      borderRadius: '4px',
                      transition: 'width 0.4s ease',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#64748b', marginTop: '4px' }}>
                  <span>{infraStats?.cloudinary?.credits?.percent_usage || 0}% ของ 25 Credits</span>
                  <span>{infraStats?.cloudinary?.liveFromWorker ? 'ดึงสดจาก Cloudinary API' : 'คำนวณประมาณการ'}</span>
                </div>
              </div>
            </div>

            {/* Badges and Footnote */}
            <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {infraStats?.cloudinary?.liveFromWorker ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span
                      style={{
                        backgroundColor: '#dcfce7',
                        color: '#15803d',
                        fontSize: '0.74em',
                        fontWeight: '700',
                        padding: '2px 8px',
                        borderRadius: '6px',
                      }}
                    >
                      ⚡ ดึงสดผ่าน Worker Secret
                    </span>
                    <span style={{ fontSize: '0.76em', color: '#475569' }}>
                      {infraStats.cloudinary.storage?.formatted}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.72em', color: '#64748b' }}>
                    แบนด์วิธ: {infraStats.cloudinary.bandwidth?.formatted}
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                  <span
                    style={{
                      backgroundColor: '#fef3c7',
                      color: '#b45309',
                      fontSize: '0.74em',
                      fontWeight: '700',
                      padding: '2px 8px',
                      borderRadius: '6px',
                    }}
                  >
                    📊 ประมาณการจากฐานข้อมูล
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowSecretModal(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#0284c7',
                      fontSize: '0.74em',
                      fontWeight: '700',
                      cursor: 'pointer',
                      padding: '2px 4px',
                      textDecoration: 'underline',
                    }}
                  >
                    ⚙️ วิธีใส่ Secret ใน Cloudflare
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Card 3: Supabase Database */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              padding: '18px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '1.2em' }}>⚡</span>
                  <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.95em' }}>
                    Supabase Database
                  </span>
                </div>
                {/* Status Pill */}
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 10px',
                    borderRadius: '12px',
                    backgroundColor: infraStats?.supabase?.online ? '#dcfce7' : (isCheckingHealth || isCheckingInfra ? '#fef3c7' : '#fee2e2'),
                    color: infraStats?.supabase?.online ? '#15803d' : (isCheckingHealth || isCheckingInfra ? '#b45309' : '#b91c1c'),
                    fontSize: '0.78em',
                    fontWeight: '700',
                  }}
                >
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: infraStats?.supabase?.online ? '#22c55e' : (isCheckingHealth || isCheckingInfra ? '#f59e0b' : '#ef4444'),
                      display: 'inline-block',
                    }}
                  />
                  {infraStats?.supabase?.online
                    ? `Online (${infraStats.supabase.latencyMs ? `${infraStats.supabase.latencyMs} ms` : 'ปกติ'})`
                    : (isCheckingHealth || isCheckingInfra ? 'กำลังตรวจสอบ...' : 'Offline')}
                </div>
              </div>

              {/* Metric */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.8em', color: '#64748b', marginBottom: '4px' }}>
                  สถานะการตอบสนองของระบบ (DB Health)
                </div>
                <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#0f172a' }}>
                  {infraStats?.supabase?.online
                    ? 'การเชื่อมต่อฐานข้อมูลปกติ'
                    : (isCheckingHealth || isCheckingInfra ? 'กำลังตรวจสอบการเชื่อมต่อ...' : 'ขัดข้อง / ไม่สามารถติดต่อได้')}
                </div>
              </div>

              {/* Details */}
              <div style={{ fontSize: '0.82em', color: '#475569', marginBottom: '12px', lineHeight: '1.5' }}>
                ฐานข้อมูล PostgreSQL บน Supabase Cloud ให้บริการจัดเก็บข้อมูลเมทาดาทาใบเสร็จ สินค้า และประวัติการใช้อย่างปลอดภัย
              </div>
            </div>

            {/* Badges and Footnote */}
            <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span
                style={{
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  fontSize: '0.74em',
                  fontWeight: '600',
                  padding: '2px 8px',
                  borderRadius: '6px',
                }}
              >
                ⏱️ Latency: {infraStats?.supabase?.latencyMs ? `${infraStats.supabase.latencyMs} ms` : 'ปกติ'}
              </span>
              <span style={{ fontSize: '0.78em', color: '#334155', fontWeight: '600' }}>
                📑 {receipts.length || stats.total} รายการใบเสร็จ
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================== */}
      {/* Dedicated Database Backup to R2 Section    */}
      {/* ========================================== */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: isMobile ? '16px' : '24px',
          border: '1px solid #e2e8f0',
          marginBottom: '24px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* Section Header */}
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: '12px',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.4em' }}>💾</span>
              <h2 style={{ fontSize: isMobile ? '1.15em' : '1.3em', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                ระบบสำรองฐานข้อมูลอัตโนมัติ (Supabase ➔ Cloudflare R2 Database Backup)
              </h2>
            </div>
            <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.86em' }}>
              สำรองสแนปช็อตฐานข้อมูลแบบ Cross-Cloud ครอบคลุม 10 ตารางหลัก ป้องกันความเสี่ยงจาก Supabase Outage พร้อมดาวน์โหลดและกู้คืนฉุกเฉิน
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setShowDrModal(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '9px 16px',
                backgroundColor: '#f1f5f9',
                color: '#334155',
                border: '1px solid #cbd5e1',
                borderRadius: '9px',
                fontSize: '0.86em',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              📖 คู่มือกู้คืนฉุกเฉิน (DR Guide)
            </button>
          </div>
        </div>

        {/* 3 Status Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '16px',
            marginBottom: '20px',
          }}
        >
          {/* Card 1: 10 ตารางที่ได้รับการคุ้มครอง */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.92em' }}>
                  🛡️ ตารางที่ได้รับการคุ้มครอง
                </span>
                <span
                  style={{
                    backgroundColor: '#dbeafe',
                    color: '#1d4ed8',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '0.75em',
                    fontWeight: '700',
                  }}
                >
                  {BACKUP_TABLES.length} ตารางสมบูรณ์
                </span>
              </div>
              <div style={{ fontSize: '0.8em', color: '#475569', marginBottom: '10px', lineHeight: '1.4' }}>
                ครอบคลุมข้อมูลคลัง, เวชภัณฑ์, ครุภัณฑ์, ใบเสร็จ, ประวัติ และผู้ใช้งาน
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {BACKUP_TABLES.map((tbl) => (
                  <span
                    key={tbl}
                    style={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #cbd5e1',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      fontSize: '0.72em',
                      color: '#334155',
                      fontFamily: 'monospace',
                    }}
                  >
                    {tbl}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '8px', marginTop: '12px', fontSize: '0.75em', color: '#64748b' }}>
              ✓ สำรองครบทุกฟิลด์ในรูปแบบ JSON Snapshot
            </div>
          </div>

          {/* Card 2: สถานะการสำรองล่าสุด */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.92em' }}>
                  ⏱️ สถานะการสำรองล่าสุด
                </span>
                <span
                  style={{
                    backgroundColor: lastBackup ? '#dcfce7' : '#fef3c7',
                    color: lastBackup ? '#15803d' : '#b45309',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '0.75em',
                    fontWeight: '700',
                  }}
                >
                  {lastBackup ? 'สำรองแล้ว' : 'รอสำรอง'}
                </span>
              </div>
              {lastBackup ? (
                <div>
                  <div style={{ fontSize: '1.25em', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>
                    {lastBackup.totalRecords ? lastBackup.totalRecords.toLocaleString() : 0}{' '}
                    <span style={{ fontSize: '0.65em', color: '#64748b', fontWeight: '500' }}>รายการ</span>
                  </div>
                  <div style={{ fontSize: '0.78em', color: '#475569', lineHeight: '1.4' }}>
                    📅 {new Date(lastBackup.timestamp).toLocaleString('th-TH')}
                  </div>
                  <div style={{ fontSize: '0.74em', color: '#64748b', marginTop: '4px', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                    📄 {lastBackup.filename}
                  </div>
                </div>
              ) : (
                <div style={{ color: '#64748b', fontSize: '0.82em', lineHeight: '1.5', padding: '8px 0' }}>
                  ยังไม่มีประวัติการสำรองในเซสชันนี้ กดปุ่มสำรองข้อมูลด้านล่างเพื่อเริ่มสร้างสแนปช็อตชุดแรก
                </div>
              )}
            </div>
            <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '8px', marginTop: '12px', fontSize: '0.75em', color: '#64748b' }}>
              🕒 อัตโนมัติ: ทุกวันเที่ยงคืนผ่าน GitHub Actions
            </div>
          </div>

          {/* Card 3: ปลายทาง Cloudflare R2 */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.92em' }}>
                  ☁️ ปลายทางที่ปลอดภัย
                </span>
                <span
                  style={{
                    backgroundColor: '#dcfce7',
                    color: '#15803d',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '0.75em',
                    fontWeight: '700',
                  }}
                >
                  R2 Disaster Recovery
                </span>
              </div>
              <div style={{ fontSize: '0.84em', color: '#0f172a', fontWeight: '600', marginBottom: '4px' }}>
                dental-documents / backups /
              </div>
              <div style={{ fontSize: '0.8em', color: '#64748b', lineHeight: '1.4' }}>
                จัดเก็บบน Cloudflare R2 แยก Data Center ออกจาก Supabase โดยสิ้นเชิง (Cross-Cloud Isolation)
              </div>
            </div>
            <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '8px', marginTop: '12px', display: 'flex', justifyContent: 'space-between', fontSize: '0.75em', color: '#64748b' }}>
              <span>🔒 Multi-Region Replicated</span>
              <span style={{ color: '#16a34a', fontWeight: '600' }}>⚡ Zero Egress Fees</span>
            </div>
          </div>
        </div>

        {/* Action Buttons Bar */}
        <div
          style={{
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            padding: '16px 20px',
            border: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: '12px',
          }}
        >
          <div>
            <div style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.92em' }}>
              ⚡ การดำเนินการสำรองฐานข้อมูล
            </div>
            <div style={{ fontSize: '0.82em', color: '#64748b', marginTop: '2px' }}>
              {backupProgressMsg || 'เลือกสำรองขึ้น Cloudflare R2 หรือบันทึกไฟล์ JSON ลงเครื่องโดยตรง'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {/* Download JSON Button */}
            <button
              type="button"
              onClick={handleDownloadBackup}
              disabled={isBackingUpR2 || isDownloadingBackup}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 18px',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                color: '#334155',
                border: '1px solid #cbd5e1',
                fontSize: '0.88em',
                fontWeight: '700',
                cursor: isBackingUpR2 || isDownloadingBackup ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {isDownloadingBackup ? (
                <>
                  <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                  กำลังดาวน์โหลด...
                </>
              ) : (
                <>
                  <span>⬇️</span> ดาวน์โหลดไฟล์สำรองลงเครื่อง (.json)
                </>
              )}
            </button>

            {/* Backup to R2 Button */}
            <button
              type="button"
              onClick={handleBackupToR2}
              disabled={isBackingUpR2 || isDownloadingBackup}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 20px',
                borderRadius: '8px',
                backgroundColor: isBackingUpR2 ? '#94a3b8' : '#059669',
                color: '#ffffff',
                border: 'none',
                fontSize: '0.88em',
                fontWeight: '700',
                cursor: isBackingUpR2 || isDownloadingBackup ? 'not-allowed' : 'pointer',
                boxShadow: isBackingUpR2 ? 'none' : '0 4px 12px rgba(5, 150, 105, 0.3)',
                transition: 'all 0.2s',
              }}
            >
              {isBackingUpR2 ? (
                <>
                  <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                  กำลังสำรองขึ้น R2...
                </>
              ) : (
                <>
                  <span>💾</span> สำรองข้อมูลลง Cloudflare R2 ทันที (Backup to R2)
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 2. Overview Stats Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {/* Card 1: เอกสารทั้งหมด */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '14px',
            padding: '20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#64748b', fontSize: '0.85em', fontWeight: '600', marginBottom: '8px' }}>
            <span>เอกสารใบเสร็จทั้งหมด</span>
            <span style={{ fontSize: '1.3em' }}>📑</span>
          </div>
          <div style={{ fontSize: '1.9em', fontWeight: '800', color: '#0f172a' }}>
            {stats.total.toLocaleString()}
          </div>
          <div style={{ fontSize: '0.78em', color: '#94a3b8', marginTop: '6px' }}>
            บันทึกการรับเข้าทั้งหมดในระบบ
          </div>
        </div>

        {/* Card 2: บน Cloudflare R2 */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '14px',
            padding: '20px',
            border: '1px solid #bbf7d0',
            boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.1)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#15803d', fontSize: '0.85em', fontWeight: '600', marginBottom: '8px' }}>
            <span>บน Cloudflare R2</span>
            <span style={{ fontSize: '1.3em' }}>🟢</span>
          </div>
          <div style={{ fontSize: '1.9em', fontWeight: '800', color: '#166534' }}>
            {stats.r2Count.toLocaleString()}
            <span style={{ fontSize: '0.5em', fontWeight: '600', color: '#15803d', marginLeft: '6px' }}>
              ({r2Percent}%)
            </span>
          </div>
          <div style={{ fontSize: '0.78em', color: '#15803d', marginTop: '6px', fontWeight: '500' }}>
            ⚡ Zero Egress Fees / ปลอดภัยสูง
          </div>
        </div>

        {/* Card 3: ตกค้างบน Cloudinary */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '14px',
            padding: '20px',
            border: stats.cloudinaryCount > 0 ? '1px solid #fed7aa' : '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(249, 115, 22, 0.1)',
            background: stats.cloudinaryCount > 0 ? 'linear-gradient(180deg, #ffffff 0%, #fff7ed 100%)' : '#ffffff',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: stats.cloudinaryCount > 0 ? '#c2410c' : '#64748b', fontSize: '0.85em', fontWeight: '600', marginBottom: '8px' }}>
            <span>ตกค้างบน Cloudinary</span>
            <span style={{ fontSize: '1.3em' }}>{stats.cloudinaryCount > 0 ? '🟠' : '✅'}</span>
          </div>
          <div style={{ fontSize: '1.9em', fontWeight: '800', color: stats.cloudinaryCount > 0 ? '#ea580c' : '#0f172a' }}>
            {stats.cloudinaryCount.toLocaleString()}
            <span style={{ fontSize: '0.5em', fontWeight: '600', color: '#c2410c', marginLeft: '6px' }}>
              ({cloudinaryPercent}%)
            </span>
          </div>
          <div style={{ fontSize: '0.78em', color: stats.cloudinaryCount > 0 ? '#c2410c' : '#10b981', marginTop: '6px', fontWeight: '500' }}>
            {stats.cloudinaryCount > 0 ? '⏳ รอการซิงค์โอนย้ายไป R2' : '✨ โอนย้ายครบทุกไฟล์แล้ว'}
          </div>
        </div>

        {/* Card 4: สถานะ R2 Gateway */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '14px',
            padding: '20px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#64748b', fontSize: '0.85em', fontWeight: '600', marginBottom: '8px' }}>
            <span>สถานะ R2 Gateway</span>
            <button
              type="button"
              onClick={handleManualRefresh}
              disabled={isCheckingHealth}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.9em',
                padding: '2px',
                color: '#64748b',
              }}
              title="ทดสอบการเชื่อมต่อใหม่"
            >
              🔄
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: workerHealth.online ? '#22c55e' : '#ef4444',
                display: 'inline-block',
                boxShadow: workerHealth.online ? '0 0 10px rgba(34, 197, 94, 0.6)' : '0 0 10px rgba(239, 68, 68, 0.6)',
              }}
            />
            <span style={{ fontSize: '1.4em', fontWeight: '800', color: workerHealth.online ? '#15803d' : '#b91c1c' }}>
              {workerHealth.online ? 'Online' : 'Offline'}
            </span>
          </div>
          <div style={{ fontSize: '0.75em', color: '#64748b', marginTop: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={workerHealth.message}>
            {isCheckingHealth ? 'กำลังทดสอบ...' : workerHealth.message}
          </div>
          <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px dashed #e2e8f0' }}>
            <button
              type="button"
              onClick={handleOpenConfigModal}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                width: '100%',
                padding: '6px 10px',
                backgroundColor: '#f0f9ff',
                color: '#0284c7',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                fontSize: '0.76em',
                fontWeight: '700',
                cursor: 'pointer',
              }}
            >
              ⚙️ ตั้งค่า URL ของ Worker
            </button>
          </div>
        </div>
      </div>

      {/* 2.5 Asset Category Selector Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '20px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: '0.9em', fontWeight: '700', color: '#475569', marginRight: '4px' }}>
          หมวดหมู่รูปภาพ:
        </span>
        {[
          { id: 'receipts', label: '🧾 ใบเสร็จรับสินค้า', catStats: receiptStats },
          { id: 'items', label: '📦 แคตตาล็อกวัสดุ (Item Master)', catStats: itemStats },
          { id: 'tags', label: '🏷️ ป้ายเครื่องมือ (Instrument Tags)', catStats: tagStats },
        ].map((cat) => {
          const isActive = assetCategory === cat.id;
          const pendingCount = cat.catStats.cloudinaryCount;
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => {
                setAssetCategory(cat.id);
                setFilterTab('all');
                setSearchQuery('');
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 18px',
                borderRadius: '12px',
                border: isActive ? '2px solid #0284c7' : '1px solid #cbd5e1',
                backgroundColor: isActive ? '#0284c7' : '#ffffff',
                color: isActive ? '#ffffff' : '#334155',
                fontWeight: '700',
                fontSize: '0.9em',
                cursor: 'pointer',
                boxShadow: isActive ? '0 3px 12px rgba(2, 132, 199, 0.25)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              <span>{cat.label}</span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '0.8em',
                  fontWeight: '700',
                  backgroundColor: isActive
                    ? 'rgba(255, 255, 255, 0.25)'
                    : pendingCount > 0
                    ? '#ffedd5'
                    : '#f1f5f9',
                  color: isActive
                    ? '#ffffff'
                    : pendingCount > 0
                    ? '#c2410c'
                    : '#64748b',
                }}
              >
                {pendingCount > 0 ? `รอโอน: ${pendingCount}` : `ครบ (${cat.catStats.total})`}
              </span>
            </button>
          );
        })}
      </div>

      {/* 3. Action & Migration Bar */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '14px',
          padding: '20px 24px',
          border: '1px solid #e2e8f0',
          marginBottom: '24px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: '16px' }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '1.1em', fontWeight: '700', color: '#0f172a' }}>
              ⚡ ซิงค์รูปภาพ{assetCategory === 'items' ? 'วัสดุ (Item Master)' : assetCategory === 'tags' ? 'ป้ายเครื่องมือ (Instrument Tags)' : 'ใบเสร็จ'}อัตโนมัติ (One-Click Auto-Migration)
            </h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.88em' }}>
              ดึงภาพต้นฉบับจาก Cloudinary อัปโหลดขึ้น Cloudflare R2 และอัปเดตฐานข้อมูล Supabase อัตโนมัติแบบทีละรายการ
            </p>
          </div>

          <button
            type="button"
            onClick={handleStartBatchMigration}
            disabled={isMigrating || stats.cloudinaryCount === 0}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px 24px',
              borderRadius: '10px',
              backgroundColor: stats.cloudinaryCount > 0 ? '#0284c7' : '#94a3b8',
              color: '#ffffff',
              border: 'none',
              fontSize: '0.95em',
              fontWeight: '700',
              cursor: isMigrating || stats.cloudinaryCount === 0 ? 'not-allowed' : 'pointer',
              boxShadow: stats.cloudinaryCount > 0 ? '0 4px 14px rgba(2, 132, 199, 0.35)' : 'none',
              transition: 'all 0.2s',
            }}
          >
            {isMigrating ? (
              <>
                <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                กำลังซิงค์ข้อมูล ({migrationProgress.current}/{migrationProgress.total})...
              </>
            ) : (
              <>
                <span>🚀</span>
                ซิงค์ย้ายรูปทั้งหมดไป Cloudflare R2 ทันที ({stats.cloudinaryCount} {assetCategory === 'items' ? 'รายการ' : assetCategory === 'tags' ? 'ป้าย' : 'ใบ'})
              </>
            )}
          </button>
        </div>

        {/* Animated Progress Bar */}
        {isMigrating && (
          <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontSize: '0.88em' }}>
              <span style={{ fontWeight: '600', color: '#0369a1' }}>
                กำลังโอนย้าย: {migrationProgress.current} จาก {migrationProgress.total} รายการ
                {migrationProgress.item?.receipt_number ? ` (ใบเสร็จ #${migrationProgress.item.receipt_number})` : migrationProgress.item?.item_name ? ` (วัสดุ: ${migrationProgress.item.item_name})` : migrationProgress.item?.name ? ` (ป้าย: ${migrationProgress.item.name})` : ''}
              </span>
              <span style={{ fontWeight: '700', color: '#0284c7' }}>
                {migrationProgress.total > 0 ? Math.round((migrationProgress.current / migrationProgress.total) * 100) : 0}%
              </span>
            </div>

            <div
              style={{
                width: '100%',
                height: '10px',
                backgroundColor: '#e0f2fe',
                borderRadius: '6px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${migrationProgress.total > 0 ? (migrationProgress.current / migrationProgress.total) * 100 : 0}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #0284c7 0%, #38bdf8 100%)',
                  borderRadius: '6px',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* 4. Filter & Search Controls */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {/* Filter Pills */}
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: isMobile ? '4px' : 0 }}>
          <button
            type="button"
            onClick={() => setFilterTab('all')}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: filterTab === 'all' ? '2px solid #0284c7' : '1px solid #cbd5e1',
              backgroundColor: filterTab === 'all' ? '#f0f9ff' : '#ffffff',
              color: filterTab === 'all' ? '#0369a1' : '#475569',
              fontWeight: '600',
              fontSize: '0.85em',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            ทั้งหมด ({stats.total})
          </button>
          <button
            type="button"
            onClick={() => setFilterTab('cloudinary')}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: filterTab === 'cloudinary' ? '2px solid #ea580c' : '1px solid #cbd5e1',
              backgroundColor: filterTab === 'cloudinary' ? '#fff7ed' : '#ffffff',
              color: filterTab === 'cloudinary' ? '#c2410c' : '#475569',
              fontWeight: '600',
              fontSize: '0.85em',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            🟠 รอโอนย้าย (Cloudinary: {stats.cloudinaryCount})
          </button>
          <button
            type="button"
            onClick={() => setFilterTab('r2')}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: filterTab === 'r2' ? '2px solid #16a34a' : '1px solid #cbd5e1',
              backgroundColor: filterTab === 'r2' ? '#f0fdf4' : '#ffffff',
              color: filterTab === 'r2' ? '#15803d' : '#475569',
              fontWeight: '600',
              fontSize: '0.85em',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            🟢 บน Cloudflare R2 ({stats.r2Count})
          </button>
        </div>

        {/* Search Input */}
        <div style={{ position: 'relative', minWidth: isMobile ? '100%' : '280px' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.9em' }}>🔍</span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={
              assetCategory === 'items'
                ? 'ค้นหาชื่อวัสดุ, หมวดหมู่, บาร์โค้ด...'
                : assetCategory === 'tags'
                ? 'ค้นหาชื่อป้าย, คำอธิบาย...'
                : 'ค้นหาเลขที่, ผู้ขาย, วันที่...'
            }
            style={{
              width: '100%',
              padding: '8px 12px 8px 34px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '0.88em',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* 5. Asset List / Table */}
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        }}
      >
        {((assetCategory === 'receipts' && filteredReceipts.length === 0) ||
          (assetCategory === 'items' && filteredItems.length === 0) ||
          (assetCategory === 'tags' && filteredTags.length === 0)) ? (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontSize: '2.5em', marginBottom: '8px' }}>📂</div>
            <div style={{ fontSize: '1em', fontWeight: '600', color: '#64748b' }}>
              ไม่พบรายการ{assetCategory === 'items' ? 'วัสดุ' : assetCategory === 'tags' ? 'ป้ายเครื่องมือ' : 'ใบเสร็จ'}ตามเงื่อนไขที่เลือก
            </div>
            <div style={{ fontSize: '0.85em', marginTop: '4px' }}>ลองปรับเปลี่ยนตัวกรอง หรือค้นหาใหม่อีกครั้ง</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {assetCategory === 'receipts' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88em' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>ภาพถ่ายหลักฐาน</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>เลขที่ใบเสร็จ / วันที่</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>ผู้จัดจำหน่าย</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>ยอดรวม (บาท)</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>แหล่งเก็บ (Storage)</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600', textAlign: 'right' }}>จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.map((rc) => {
                    const provider = getStorageProvider(rc.receipt_image_url);
                    const isCloudinary = provider === 'Cloudinary';
                    const isR2 = provider === 'Cloudflare R2';
                    const isMigratingThis = singleMigratingId === rc.id;

                    return (
                      <tr
                        key={rc.id}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          transition: 'background-color 0.15s',
                        }}
                      >
                        {/* Thumbnail */}
                        <td style={{ padding: '10px 16px' }}>
                          {rc.receipt_image_url ? (
                            <div
                              onClick={() => setZoomedImage({ url: rc.receipt_image_url, receiptNumber: rc.receipt_number })}
                              style={{
                                width: '54px',
                                height: '54px',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '1px solid #e2e8f0',
                                cursor: 'pointer',
                                position: 'relative',
                                backgroundColor: '#f8fafc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              title="คลิกเพื่อซูมดูรูปภาพ"
                            >
                              <img
                                src={rc.receipt_image_url}
                                alt={`Receipt #${rc.receipt_number}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '2px',
                                  right: '2px',
                                  backgroundColor: 'rgba(0,0,0,0.5)',
                                  color: '#ffffff',
                                  borderRadius: '4px',
                                  padding: '1px 3px',
                                  fontSize: '0.65em',
                                }}
                              >
                                🔍
                              </div>
                            </div>
                          ) : (
                            <div
                              style={{
                                width: '54px',
                                height: '54px',
                                borderRadius: '8px',
                                border: '1px dashed #cbd5e1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#94a3b8',
                                fontSize: '0.7em',
                                textAlign: 'center',
                              }}
                            >
                              ไม่มีรูป
                            </div>
                          )}
                        </td>

                        {/* เลขที่ & วันที่ */}
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ fontWeight: '700', color: '#0f172a' }}>
                            #{rc.receipt_number || 'ไม่ระบุ'}
                          </div>
                          <div style={{ fontSize: '0.8em', color: '#64748b', marginTop: '2px' }}>
                            📅 {rc.received_date || '-'}
                          </div>
                        </td>

                        {/* ผู้จัดจำหน่าย */}
                        <td style={{ padding: '10px 16px', color: '#334155' }}>
                          {rc.supplier_name || '-'}
                        </td>

                        {/* ยอดรวม */}
                        <td style={{ padding: '10px 16px', fontWeight: '600', color: '#0f172a' }}>
                          ฿{Number(rc.total_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                        </td>

                        {/* แหล่งเก็บ (Badge) */}
                        <td style={{ padding: '10px 16px' }}>
                          {isR2 && (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                backgroundColor: '#f0fdf4',
                                color: '#15803d',
                                border: '1px solid #bbf7d0',
                                fontSize: '0.8em',
                                fontWeight: '600',
                              }}
                            >
                              <span>🟢</span> Cloudflare R2
                            </span>
                          )}
                          {isCloudinary && (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                backgroundColor: '#fff7ed',
                                color: '#c2410c',
                                border: '1px solid #fed7aa',
                                fontSize: '0.8em',
                                fontWeight: '600',
                              }}
                            >
                              <span>🟠</span> Cloudinary (รอโอน)
                            </span>
                          )}
                          {!isR2 && !isCloudinary && (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                backgroundColor: '#f1f5f9',
                                color: '#64748b',
                                fontSize: '0.8em',
                                fontWeight: '600',
                              }}
                            >
                              ⚪ อื่นๆ
                            </span>
                          )}
                        </td>

                        {/* Action Buttons */}
                        <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '6px' }}>
                            {isCloudinary && (
                              <button
                                type="button"
                                onClick={() => handleMigrateSingle(rc)}
                                disabled={isMigratingThis || isMigrating}
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  backgroundColor: '#0284c7',
                                  color: '#ffffff',
                                  border: 'none',
                                  fontSize: '0.82em',
                                  fontWeight: '600',
                                  cursor: isMigratingThis || isMigrating ? 'not-allowed' : 'pointer',
                                }}
                                title="โอนย้ายใบเสร็จนี้ไป Cloudflare R2"
                              >
                                {isMigratingThis ? '⏳ กำลังย้าย...' : '🔄 ย้ายไป R2'}
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => openReplaceModal(rc)}
                              style={{
                                padding: '6px 10px',
                                borderRadius: '6px',
                                backgroundColor: '#f8fafc',
                                color: '#475569',
                                border: '1px solid #cbd5e1',
                                fontSize: '0.82em',
                                fontWeight: '600',
                                cursor: 'pointer',
                              }}
                              title="อัปโหลดรูปภาพใหม่ทดแทน"
                            >
                              ✏️ เปลี่ยนรูป
                            </button>

                            {rc.receipt_image_url && (
                              <button
                                type="button"
                                onClick={() => setZoomedImage({ url: rc.receipt_image_url, receiptNumber: rc.receipt_number })}
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  backgroundColor: '#f8fafc',
                                  color: '#0284c7',
                                  border: '1px solid #cbd5e1',
                                  fontSize: '0.82em',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                }}
                                title="ดูรูปภาพขนาดใหญ่"
                              >
                                🔍 ดูรูป
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {assetCategory === 'items' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88em' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>รูปภาพวัสดุ</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>ชื่อวัสดุ / หมวดหมู่</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>บาร์โค้ด / หน่วยนับ</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>แหล่งเก็บ (Storage)</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600', textAlign: 'right' }}>จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((it) => {
                    const rawUrls = getItemImageUrls(it?.image_url);
                    const urls = Array.isArray(rawUrls) ? rawUrls : [];
                    const hasCloudinary = urls.some((u) => isCloudinaryUrl(u));
                    const allR2 = urls.length > 0 && urls.every((u) => isR2Url(u));
                    const isMigratingThis = singleMigratingId === it.id;
                    const firstUrl = urls[0];

                    return (
                      <tr key={it.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 16px' }}>
                          {urls.length > 0 ? (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {urls.slice(0, 4).map((url, idx) => (
                                <div
                                  key={idx}
                                  onClick={() => setZoomedImage({ url, receiptNumber: it.item_name })}
                                  style={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '6px',
                                    overflow: 'hidden',
                                    border: '1px solid #e2e8f0',
                                    cursor: 'pointer',
                                    backgroundColor: '#f8fafc',
                                  }}
                                  title={`คลิกเพื่อดูรูป (${idx + 1})`}
                                >
                                  <img src={url} alt={it.item_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ width: '44px', height: '44px', borderRadius: '6px', border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.7em' }}>
                              ไม่มีรูป
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ fontWeight: '700', color: '#0f172a' }}>{it.item_name}</div>
                          <div style={{ fontSize: '0.8em', color: '#64748b', marginTop: '2px' }}>🏷️ {it.category || '-'}</div>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ fontFamily: 'monospace', fontSize: '0.9em', color: '#334155' }}>{it.barcode || '-'}</div>
                          <div style={{ fontSize: '0.8em', color: '#64748b', marginTop: '2px' }}>หน่วย: {it.unit || '-'}</div>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          {allR2 && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', fontSize: '0.8em', fontWeight: '600' }}>
                              <span>🟢</span> Cloudflare R2
                            </span>
                          )}
                          {hasCloudinary && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', backgroundColor: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', fontSize: '0.8em', fontWeight: '600' }}>
                              <span>🟠</span> Cloudinary (รอโอน)
                            </span>
                          )}
                          {!allR2 && !hasCloudinary && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', backgroundColor: '#f1f5f9', color: '#64748b', fontSize: '0.8em', fontWeight: '600' }}>
                              ⚪ อื่นๆ / ไม่มีรูป
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '6px' }}>
                            {hasCloudinary && (
                              <button
                                type="button"
                                onClick={() => handleMigrateSingle(it)}
                                disabled={isMigratingThis || isMigrating}
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  backgroundColor: '#0284c7',
                                  color: '#ffffff',
                                  border: 'none',
                                  fontSize: '0.82em',
                                  fontWeight: '600',
                                  cursor: isMigratingThis || isMigrating ? 'not-allowed' : 'pointer',
                                }}
                                title="โอนย้ายรูปวัสดุนี้ไป Cloudflare R2"
                              >
                                {isMigratingThis ? '⏳ กำลังย้าย...' : '🔄 ย้ายไป R2'}
                              </button>
                            )}
                            {firstUrl && (
                              <button
                                type="button"
                                onClick={() => setZoomedImage({ url: firstUrl, receiptNumber: it.item_name })}
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  backgroundColor: '#f8fafc',
                                  color: '#0284c7',
                                  border: '1px solid #cbd5e1',
                                  fontSize: '0.82em',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                }}
                                title="ดูรูปภาพขนาดใหญ่"
                              >
                                🔍 ดูรูป
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {assetCategory === 'tags' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88em' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>รูปภาพป้าย</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>ชื่อป้ายเครื่องมือ</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>คำอธิบาย</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600' }}>แหล่งเก็บ (Storage)</th>
                    <th style={{ padding: '12px 16px', fontWeight: '600', textAlign: 'right' }}>จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTags.map((tg) => {
                    const isCloudinary = isCloudinaryUrl(tg.image_url);
                    const isR2 = isR2Url(tg.image_url);
                    const isMigratingThis = singleMigratingId === tg.id;

                    return (
                      <tr key={tg.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 16px' }}>
                          {tg.image_url ? (
                            <div
                              onClick={() => setZoomedImage({ url: tg.image_url, receiptNumber: tg.name })}
                              style={{
                                width: '54px',
                                height: '54px',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '1px solid #e2e8f0',
                                cursor: 'pointer',
                                backgroundColor: '#f8fafc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              title="คลิกเพื่อดูรูปภาพ"
                            >
                              <img src={tg.image_url} alt={tg.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                          ) : (
                            <div style={{ width: '54px', height: '54px', borderRadius: '8px', border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.7em' }}>
                              ไม่มีรูป
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ fontWeight: '700', color: '#0f172a' }}>{tg.name}</div>
                        </td>
                        <td style={{ padding: '10px 16px', color: '#475569', fontSize: '0.85em', maxWidth: '350px' }}>
                          {tg.description || '-'}
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          {isR2 && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', fontSize: '0.8em', fontWeight: '600' }}>
                              <span>🟢</span> Cloudflare R2
                            </span>
                          )}
                          {isCloudinary && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', backgroundColor: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', fontSize: '0.8em', fontWeight: '600' }}>
                              <span>🟠</span> Cloudinary (รอโอน)
                            </span>
                          )}
                          {!isR2 && !isCloudinary && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '12px', backgroundColor: '#f1f5f9', color: '#64748b', fontSize: '0.8em', fontWeight: '600' }}>
                              ⚪ อื่นๆ / ไม่มีรูป
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '6px' }}>
                            {isCloudinary && (
                              <button
                                type="button"
                                onClick={() => handleMigrateSingle(tg)}
                                disabled={isMigratingThis || isMigrating}
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  backgroundColor: '#0284c7',
                                  color: '#ffffff',
                                  border: 'none',
                                  fontSize: '0.82em',
                                  fontWeight: '600',
                                  cursor: isMigratingThis || isMigrating ? 'not-allowed' : 'pointer',
                                }}
                                title="โอนย้ายป้ายนี้ไป Cloudflare R2"
                              >
                                {isMigratingThis ? '⏳ กำลังย้าย...' : '🔄 ย้ายไป R2'}
                              </button>
                            )}
                            {tg.image_url && (
                              <button
                                type="button"
                                onClick={() => setZoomedImage({ url: tg.image_url, receiptNumber: tg.name })}
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  backgroundColor: '#f8fafc',
                                  color: '#0284c7',
                                  border: '1px solid #cbd5e1',
                                  fontSize: '0.82em',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                }}
                                title="ดูรูปภาพขนาดใหญ่"
                              >
                                🔍 ดูรูป
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/* MODAL 1: เปลี่ยนรูปภาพใบเสร็จ (Replace Image Modal) */}
      {/* ============================================================ */}
      {replaceTarget && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '520px',
              width: '100%',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.2em', fontWeight: '700', color: '#0f172a' }}>
                ✏️ เปลี่ยนรูปภาพเอกสารใบเสร็จ
              </h3>
              <button
                type="button"
                onClick={() => setReplaceTarget(null)}
                style={{ background: 'none', border: 'none', fontSize: '1.2em', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            <div style={{ fontSize: '0.9em', color: '#64748b', marginBottom: '16px' }}>
              ใบเสร็จเลขที่: <strong>#{replaceTarget.receipt_number || replaceTarget.id}</strong>
              <br />
              รูปภาพใหม่จะถูกอัปโหลดตรงไปยัง <strong>Cloudflare R2</strong> โดยอัตโนมัติ
            </div>

            {/* File Input Zone */}
            <div
              style={{
                border: '2px dashed #cbd5e1',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#f8fafc',
                cursor: 'pointer',
                marginBottom: '16px',
              }}
              onClick={() => document.getElementById('replace-file-input')?.click()}
            >
              <input
                id="replace-file-input"
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />

              {replacementPreview ? (
                <div>
                  <img
                    src={replacementPreview}
                    alt="Preview"
                    style={{ maxHeight: '180px', maxWidth: '100%', borderRadius: '8px', objectFit: 'contain' }}
                  />
                  <div style={{ fontSize: '0.8em', color: '#16a34a', marginTop: '8px', fontWeight: '600' }}>
                    ✓ เลือกไฟล์: {replacementFile?.name}
                  </div>
                </div>
              ) : replacementFile ? (
                <div style={{ color: '#16a34a', fontWeight: '600' }}>
                  📄 {replacementFile.name} ({(replacementFile.size / 1024).toFixed(1)} KB)
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '2em', marginBottom: '8px' }}>📤</div>
                  <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '0.95em' }}>
                    คลิกเพื่อเลือกไฟล์รูปภาพใหม่
                  </div>
                  <div style={{ fontSize: '0.8em', color: '#94a3b8', marginTop: '4px' }}>
                    รองรับไฟล์ JPG, PNG, WebP หรือ PDF
                  </div>
                </div>
              )}
            </div>

            {replaceError && (
              <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85em', marginBottom: '16px' }}>
                ⚠️ {replaceError}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setReplaceTarget(null)}
                disabled={isReplacing}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  color: '#475569',
                  fontWeight: '600',
                  cursor: isReplacing ? 'not-allowed' : 'pointer',
                }}
              >
                ยกเลิก
              </button>

              <button
                type="button"
                onClick={handleConfirmReplace}
                disabled={isReplacing || !replacementFile}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: isReplacing || !replacementFile ? '#94a3b8' : '#0284c7',
                  color: '#ffffff',
                  fontWeight: '700',
                  cursor: isReplacing || !replacementFile ? 'not-allowed' : 'pointer',
                }}
              >
                {isReplacing ? 'กำลังอัปโหลด...' : '🚀 อัปโหลดไป R2'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL 2: ซูมดูภาพขนาดใหญ่ (Zoom Modal) */}
      {/* ============================================================ */}
      {zoomedImage && (
        <div
          onClick={() => setZoomedImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.92)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: '20px',
            cursor: 'zoom-out',
          }}
        >
          {/* Controls Bar */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: 'rgba(15, 23, 42, 0.8)',
              padding: '8px 16px',
              borderRadius: '24px',
              color: '#ffffff',
              cursor: 'default',
            }}
          >
            <span style={{ fontSize: '0.9em', fontWeight: '600' }}>
              ใบเสร็จ #{zoomedImage.receiptNumber || 'เอกสาร'}
            </span>

            <button
              type="button"
              onClick={() => handleDownloadImage(zoomedImage.url, `receipt-${zoomedImage.receiptNumber || 'evidence'}.jpg`)}
              style={{
                backgroundColor: '#16a34a',
                color: '#ffffff',
                border: 'none',
                borderRadius: '16px',
                padding: '4px 12px',
                fontSize: '0.82em',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              💾 ดาวน์โหลด
            </button>

            <button
              type="button"
              onClick={() => setZoomedImage(null)}
              style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '26px',
                height: '26px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>

          <img
            src={zoomedImage.url}
            alt="Zoomed Receipt Document"
            style={{
              maxWidth: '92vw',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
          />
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL: วิธีตั้งค่า Cloudinary Secret ใน Cloudflare Worker */}
      {/* ============================================================ */}
      {showSecretModal && (
        <div
          onClick={() => setShowSecretModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: '20px',
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '650px',
              width: '100%',
              padding: '28px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '1.25em', fontWeight: '800', color: '#0f172a' }}>
                ⚙️ วิธีใส่ Secret ใน Cloudflare เพื่อดึงโควต้าสด
              </h3>
              <button
                type="button"
                onClick={() => setShowSecretModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2em',
                  cursor: 'pointer',
                  color: '#64748b',
                }}
              >
                ✕
              </button>
            </div>

            <p style={{ color: '#64748b', fontSize: '0.88em', lineHeight: '1.6', marginBottom: '20px' }}>
              เพื่อความปลอดภัยสูงสุด Frontend จะไม่ถือหรือเปิดเผย Cloudinary API Secret เด็ดขาด ระบบจะทำงานผ่าน <strong>Cloudflare Worker</strong> ในฐานะ Proxy ปลอดภัย เพียงนำ API Key และ Secret ไปวางใน Worker Dashboard:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ backgroundColor: '#0284c7', color: '#ffffff', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8em', fontWeight: 'bold', flexShrink: 0, marginTop: '2px' }}>1</span>
                <div style={{ fontSize: '0.88em', color: '#334155', lineHeight: '1.5' }}>
                  เข้าสู่ระบบ <strong>Cloudflare Dashboard</strong> &gt; เมนู <strong>Compute (Workers & Pages)</strong> &gt; เลือก Worker ของคุณ (เช่น <code>dental-r2-uploader</code>)
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ backgroundColor: '#0284c7', color: '#ffffff', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8em', fontWeight: 'bold', flexShrink: 0, marginTop: '2px' }}>2</span>
                <div style={{ fontSize: '0.88em', color: '#334155', lineHeight: '1.5' }}>
                  ไปที่แท็บ <strong>Settings</strong> &gt; หัวข้อ <strong>Variables and Secrets</strong>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ backgroundColor: '#0284c7', color: '#ffffff', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8em', fontWeight: 'bold', flexShrink: 0, marginTop: '2px' }}>3</span>
                <div style={{ fontSize: '0.88em', color: '#334155', lineHeight: '1.5' }}>
                  กด <strong>Add</strong> เพิ่มค่าต่อไปนี้ (เลือกแบบ <strong>Secret / Encrypt</strong>):
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
                    <li><code>CLOUDINARY_CLOUD_NAME</code> = <code>dwb0oda9m</code></li>
                    <li><code>CLOUDINARY_API_KEY</code> = <em>คัดลอกจาก Cloudinary Console</em></li>
                    <li><code>CLOUDINARY_API_SECRET</code> = <em>คัดลอกจาก Cloudinary Console</em></li>
                  </ul>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ backgroundColor: '#0284c7', color: '#ffffff', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8em', fontWeight: 'bold', flexShrink: 0, marginTop: '2px' }}>4</span>
                <div style={{ fontSize: '0.88em', color: '#334155', lineHeight: '1.5' }}>
                  คลิก <strong>Deploy</strong> แล้วกลับมากดปุ่ม <strong>"รีเฟรชสถานะสด"</strong> ที่หน้านี้เพื่อดูข้อมูลสดทันที!
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowSecretModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#0284c7',
                  color: '#ffffff',
                  fontWeight: '700',
                  fontSize: '0.9em',
                  cursor: 'pointer',
                }}
              >
                เข้าใจแล้ว ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL 4: กำหนด URL ของ Cloudflare Worker */}
      {/* ============================================================ */}
      {showConfigModal && (
        <div
          onClick={() => setShowConfigModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: '20px',
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '560px',
              width: '100%',
              padding: '28px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.4em' }}>⚙️</span>
                <h3 style={{ margin: 0, fontSize: '1.25em', fontWeight: '800', color: '#0f172a' }}>
                  กำหนด URL ของ Cloudflare Worker
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowConfigModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2em',
                  cursor: 'pointer',
                  color: '#64748b',
                }}
              >
                ✕
              </button>
            </div>

            {/* Current Active Source Indicator */}
            <div style={{ marginBottom: '18px', padding: '10px 14px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '0.84em' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#64748b' }}>URL ที่กำลังใช้งานในระบบ:</span>
                {typeof window !== 'undefined' && window.localStorage?.getItem('R2_WORKER_URL') ? (
                  <span style={{ backgroundColor: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: '6px', fontWeight: '700', fontSize: '0.85em' }}>
                    💾 บันทึกใน LocalStorage
                  </span>
                ) : getR2WorkerUrl() ? (
                  <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '6px', fontWeight: '700', fontSize: '0.85em' }}>
                    📄 ค่าจากไฟล์ .env
                  </span>
                ) : (
                  <span style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '2px 8px', borderRadius: '6px', fontWeight: '700', fontSize: '0.85em' }}>
                    ⚠️ ยังไม่ได้กำหนด
                  </span>
                )}
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.88em', color: '#0f172a', marginTop: '4px', wordBreak: 'break-all' }}>
                {getR2WorkerUrl() || '(ยังไม่ได้กำหนด URL ของ Worker ในระบบ)'}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSaveConfig}>
              {/* Field 1: Worker Base URL */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  htmlFor="worker-url-input"
                  style={{ display: 'block', fontSize: '0.88em', fontWeight: '700', color: '#0f172a', marginBottom: '6px' }}
                >
                  Worker Base URL <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  id="worker-url-input"
                  type="url"
                  value={workerUrlInput || ''}
                  onChange={(e) => setWorkerUrlInput(e.target.value)}
                  placeholder="https://dental-r2-uploader.nuntsak.workers.dev"
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9em',
                    outline: 'none',
                    boxSizing: 'border-box',
                    fontFamily: 'monospace',
                  }}
                />
                <div style={{ fontSize: '0.78em', color: '#64748b', marginTop: '4px' }}>
                  ตัวอย่าง: <code>https://dental-r2-uploader.nuntsak.workers.dev</code> (ไม่ต้องใส่ <code>/upload</code> ต่อท้าย)
                </div>
              </div>

              {/* Field 2: Custom Auth Key */}
              <div style={{ marginBottom: '18px' }}>
                <label
                  htmlFor="worker-auth-key-input"
                  style={{ display: 'block', fontSize: '0.88em', fontWeight: '700', color: '#0f172a', marginBottom: '6px' }}
                >
                  X-Custom-Auth-Key (ถ้ามี / ไม่บังคับ)
                </label>
                <input
                  id="worker-auth-key-input"
                  type="text"
                  value={workerAuthKeyInput || ''}
                  onChange={(e) => setWorkerAuthKeyInput(e.target.value)}
                  placeholder="เว้นว่างไว้หาก Worker ไม่ได้ตั้งรหัสผ่าน Auth Key"
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.9em',
                    outline: 'none',
                    boxSizing: 'border-box',
                    fontFamily: 'monospace',
                  }}
                />
                <div style={{ fontSize: '0.78em', color: '#64748b', marginTop: '4px' }}>
                  รหัสผ่านที่ส่งใน Header <code>X-Custom-Auth-Key</code> เพื่อป้องกันบุคคลภายนอกเรียกใช้งาน Worker
                </div>
              </div>

              {/* Informational Note as required */}
              <div
                style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  fontSize: '0.84em',
                  color: '#0369a1',
                  lineHeight: '1.5',
                  marginBottom: '20px',
                }}
              >
                💡 คุณสามารถตั้งค่าผ่านหน้านี้ได้ทันที หรือแก้ไขในไฟล์ <code>.env</code> แล้วรีสตาร์ตเซิร์ฟเวอร์ (<code>npm start</code> ใหม่)
              </div>

              {/* Error Message */}
              {configErrorMsg && (
                <div
                  style={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#b91c1c',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    fontSize: '0.85em',
                    marginBottom: '16px',
                  }}
                >
                  ⚠️ {configErrorMsg}
                </div>
              )}

              {/* Success Message */}
              {configSuccessMsg && (
                <div
                  style={{
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    color: '#15803d',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    fontSize: '0.85em',
                    marginBottom: '16px',
                    fontWeight: '600',
                  }}
                >
                  ✓ {configSuccessMsg}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  {typeof window !== 'undefined' && window.localStorage?.getItem('R2_WORKER_URL') && (
                    <button
                      type="button"
                      onClick={handleClearConfig}
                      disabled={isSavingConfig}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        color: '#64748b',
                        fontSize: '0.82em',
                        cursor: isSavingConfig ? 'not-allowed' : 'pointer',
                      }}
                    >
                      ล้างค่าในเครื่อง (คืนค่า .env)
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setShowConfigModal(false)}
                    disabled={isSavingConfig}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      backgroundColor: '#ffffff',
                      color: '#475569',
                      fontWeight: '600',
                      fontSize: '0.88em',
                      cursor: isSavingConfig ? 'not-allowed' : 'pointer',
                    }}
                  >
                    ยกเลิก
                  </button>

                  <button
                    type="submit"
                    disabled={isSavingConfig}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#0284c7',
                      color: '#ffffff',
                      fontWeight: '700',
                      fontSize: '0.88em',
                      cursor: isSavingConfig ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 4px 12px rgba(2, 132, 199, 0.3)',
                    }}
                  >
                    {isSavingConfig ? 'กำลังบันทึกและทดสอบ...' : '💾 บันทึกและทดสอบเชื่อมต่อ'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Backup Success Details */}
      {backupModalData && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onClick={() => setBackupModalData(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '650px',
              width: '100%',
              padding: '24px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.4em' }}>🎉</span>
                <h3 style={{ margin: 0, fontSize: '1.25em', fontWeight: '800', color: '#0f172a' }}>
                  สำรองฐานข้อมูลขึ้น Cloudflare R2 สำเร็จ!
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setBackupModalData(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2em',
                  cursor: 'pointer',
                  color: '#64748b',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#166534', fontSize: '0.88em' }}>
              สแนปช็อตข้อมูลทั้ง 10 ตารางถูกส่งไปจัดเก็บบน Cloudflare R2 บัคเก็ต <code>dental-documents/backups/</code> เรียบร้อยแล้ว
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px', fontSize: '0.86em' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                <span style={{ color: '#64748b', fontWeight: '600' }}>ชื่อไฟล์สแนปช็อต:</span>
                <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#0f172a' }}>{backupModalData.filename}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                <span style={{ color: '#64748b', fontWeight: '600' }}>จำนวนรายการทั้งหมด:</span>
                <span style={{ fontWeight: '800', color: '#059669' }}>{backupModalData.totalRecords?.toLocaleString()} รายการ</span>
              </div>
              {backupModalData.url && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                  <span style={{ color: '#64748b', fontWeight: '600' }}>R2 Public URL:</span>
                  <a
                    href={backupModalData.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#0284c7', wordBreak: 'break-all', fontSize: '0.85em', fontFamily: 'monospace' }}
                  >
                    {backupModalData.url}
                  </a>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '0.88em', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>
                📊 สรุปจำนวนข้อมูลรายตาราง (Table Row Breakdown):
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {backupModalData.tableCounts &&
                  Object.entries(backupModalData.tableCounts).map(([tbl, count]) => (
                    <div
                      key={tbl}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '6px 12px',
                        backgroundColor: '#f1f5f9',
                        borderRadius: '6px',
                        fontSize: '0.8em',
                        fontFamily: 'monospace',
                      }}
                    >
                      <span style={{ color: '#334155' }}>{tbl}</span>
                      <span style={{ fontWeight: '700', color: '#0f172a' }}>{count.toLocaleString()} แถว</span>
                    </div>
                  ))}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setBackupModalData(null)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#0284c7',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '700',
                  fontSize: '0.9em',
                  cursor: 'pointer',
                }}
              >
                ตกลง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Disaster Recovery Guide */}
      {showDrModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onClick={() => setShowDrModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '750px',
              width: '100%',
              padding: '24px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.4em' }}>📖</span>
                <h3 style={{ margin: 0, fontSize: '1.25em', fontWeight: '800', color: '#0f172a' }}>
                  คู่มือกู้คืนข้อมูลกรณีฉุกเฉิน (Disaster Recovery Guide)
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowDrModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2em',
                  cursor: 'pointer',
                  color: '#64748b',
                }}
              >
                ✕
              </button>
            </div>

            <p style={{ color: '#475569', fontSize: '0.88em', marginTop: 0, marginBottom: '16px', lineHeight: '1.5' }}>
              เมื่อเกิดเหตุการณ์ไม่คาดฝันกับ Supabase เช่น Cloud Service ล่มเป็นเวลานาน หรือข้อมูลสูญหาย สามารถกู้คืนข้อมูลได้ตามขั้นตอนต่อไปนี้:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
                <div style={{ fontWeight: '700', color: '#0369a1', fontSize: '0.92em', marginBottom: '4px' }}>
                  1. ดาวน์โหลดไฟล์สำรองล่าสุดจาก Cloudflare R2
                </div>
                <div style={{ color: '#334155', fontSize: '0.84em', lineHeight: '1.5' }}>
                  เข้าสู่ Cloudflare Dashboard ไปที่ <b>R2 Object Storage</b> &gt; บัคเก็ต <code>dental-documents</code> &gt; โฟลเดอร์ <code>backups/</code> เลือกไฟล์สแนปช็อตล่าสุด (เช่น <code>dental_backup_2026-09-07T...json</code>) หรือกดปุ่ม &quot;ดาวน์โหลดไฟล์สำรองลงเครื่อง&quot; ได้จากหน้านี้
                </div>
              </div>

              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
                <div style={{ fontWeight: '700', color: '#0369a1', fontSize: '0.92em', marginBottom: '4px' }}>
                  2. โครงสร้างไฟล์ Snapshot JSON
                </div>
                <div style={{ color: '#334155', fontSize: '0.84em', lineHeight: '1.5' }}>
                  ไฟล์ JSON บรรจุข้อมูลแยกตามชื่อตารางทั้ง 10 ตารางในฟิลด์ <code>tables</code> (<code>item_master</code>, <code>supplies</code>, <code>instruments</code>, <code>orders</code>, <code>logs</code>, <code>receipts</code>, <code>receipt_items</code>, <code>instrument_tags</code>, <code>sterile_options</code>, <code>user_profiles</code>)
                </div>
              </div>

              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
                <div style={{ fontWeight: '700', color: '#0369a1', fontSize: '0.92em', marginBottom: '4px' }}>
                  3. นำเข้าข้อมูล (Restore Process)
                </div>
                <div style={{ color: '#334155', fontSize: '0.84em', lineHeight: '1.5' }}>
                  • <b>กรณี Supabase เดิมกู้คืนได้:</b> นำเข้าผ่าน SQL Editor หรือ REST API โดยเรียงลำดับ Parent tables ก่อน Child tables (เช่น <code>item_master</code> ก่อน <code>supplies</code> และ <code>receipts</code> ก่อน <code>receipt_items</code>)<br />
                  • <b>กรณีสร้าง Supabase Project สำรองใหม่:</b> นำเข้า Database Schema DDL แล้วนำเข้าชุดข้อมูล JSON เข้าสู่ตารางตามลำดับ
                </div>
              </div>

              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
                <div style={{ fontWeight: '700', color: '#0369a1', fontSize: '0.92em', marginBottom: '4px' }}>
                  4. การสลับ Connection ไปยัง Database ใหม่ (Failover)
                </div>
                <div style={{ color: '#334155', fontSize: '0.84em', lineHeight: '1.5' }}>
                  แก้ไขค่า <code>REACT_APP_SUPABASE_URL</code> และ <code>REACT_APP_SUPABASE_ANON_KEY</code> ใน <code>.env</code> หรือ Deployment Environment ระบบจะเชื่อมต่อกับ Database ใหม่ทันที โดยไฟล์รูปภาพบน Cloudflare R2 ทั้งหมดยังคงใช้งานได้ตามปกติโดยไม่ต้องย้ายรูป
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowDrModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#0284c7',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '700',
                  fontSize: '0.9em',
                  cursor: 'pointer',
                }}
              >
                เข้าใจแล้ว / ปิด
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorageAdminPage;
